import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Login, Register, Onboarding, ForgotPassword, ResetPassword } from "@/pages/Auth";
import HomePage from "@/pages/Home";
import GamesPage from "@/pages/Games";
import GameDetailPage from "@/pages/GameDetail";
import TeamsPage from "@/pages/Teams";
import PlayersPage from "@/pages/Players";
import StandingsPage from "@/pages/Standings";
import TeamDetailPage from "@/pages/TeamDetail";
import PlayerDetailPage from "@/pages/PlayerDetail";
import RoomsPage from "@/pages/Rooms";
import RoomDetailPage from "@/pages/RoomDetail";
import MessagesPage from "@/pages/Messages";
import PredictPage from "@/pages/Predict";
import ProfilePage from "@/pages/Profile";
import TailgatesPage from "@/pages/Tailgates";
import LivePage from "@/pages/Live";
import LiveRoomPage from "@/pages/LiveRoom";
import RehearsalPage from "@/pages/Rehearsal";
import AdminPage from "@/pages/Admin";
import PartnerPregamePage from "@/pages/PartnerPregame";
import LeaguesPage from "@/pages/Leagues";
import ShopPage from "@/pages/Shop";
import ModerationPage from "@/pages/Moderation";
import NotificationsPage from "@/pages/Notifications";
import InviteAcceptPage from "@/pages/InviteAccept";
import Landing from "@/pages/Landing";
import OwnerClaim from "@/pages/OwnerClaim";
import { IS_PRELAUNCH } from "@/config/launch";

function FullLoader() {
  return (
    <div className="min-h-screen grid place-items-center bg-[#10151D]">
      <div className="text-center">
        <div className="w-10 h-10 rounded-xl bg-[#2F7D32] grid place-items-center font-display text-white text-2xl mx-auto mb-3 animate-pulse">T</div>
        <div className="text-white/40 text-sm">Loading Turf Talk…</div>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  // Access control. In prelaunch, unauthenticated visitors are sent to the public
  // front door (/); in live mode they go to the standard /login. Either way the
  // intended deep link is preserved via ?next= so it resolves after sign-in.
  if (!user) {
    const nextParam = encodeURIComponent(location.pathname + location.search);
    const target = IS_PRELAUNCH ? `/?next=${nextParam}` : `/login?next=${nextParam}`;
    return <Navigate to={target} replace />;
  }
  if (user.onboarding_complete === false && location.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  return children;
}

// Root gate — the single lifecycle branch point.
//  • Authenticated users ALWAYS get the full interior app home (unchanged).
//  • Prelaunch + unauthenticated → public front-door landing (additive shell).
//  • Live + unauthenticated → standard /login (shell bypassed).
function RootGate() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (!user) return IS_PRELAUNCH ? <Landing /> : <Navigate to={`/login${location.search}`} replace />;
  if (user.onboarding_complete === false) return <Navigate to="/onboarding" replace />;
  return <Shell><HomePage /></Shell>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user && user.onboarding_complete !== false) return <Navigate to="/" replace />;
  return children;
}

function Shell({ children }) {
  return <AppShell>{children}</AppShell>;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster theme="dark" position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/forgot" element={<ForgotPassword />} />
          <Route path="/reset" element={<ResetPassword />} />
          <Route path="/owner-claim" element={<OwnerClaim />} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />

          <Route path="/" element={<RootGate />} />
          <Route path="/games" element={<Protected><Shell><GamesPage /></Shell></Protected>} />
          <Route path="/game/:id" element={<Protected><Shell><GameDetailPage /></Shell></Protected>} />
          <Route path="/teams" element={<Protected><Shell><TeamsPage /></Shell></Protected>} />
          <Route path="/players" element={<Protected><Shell><PlayersPage /></Shell></Protected>} />
          <Route path="/standings" element={<Protected><Shell><StandingsPage /></Shell></Protected>} />
          <Route path="/team/:id" element={<Protected><Shell><TeamDetailPage /></Shell></Protected>} />
          <Route path="/player/:id" element={<Protected><Shell><PlayerDetailPage /></Shell></Protected>} />
          <Route path="/rooms" element={<Protected><Shell><RoomsPage /></Shell></Protected>} />
          <Route path="/room/:id" element={<Protected><Shell><RoomDetailPage /></Shell></Protected>} />
          <Route path="/messages" element={<Protected><Shell><MessagesPage /></Shell></Protected>} />
          <Route path="/connections" element={<Protected><Shell><MessagesPage /></Shell></Protected>} />
          <Route path="/predict" element={<Protected><Shell><PredictPage /></Shell></Protected>} />
          <Route path="/leagues" element={<Protected><Shell><LeaguesPage /></Shell></Protected>} />
          <Route path="/shop" element={<Protected><Shell><ShopPage /></Shell></Protected>} />
          <Route path="/moderation" element={<Protected><Shell><ModerationPage /></Shell></Protected>} />
          <Route path="/invite/:kind/:token" element={<Protected><Shell><InviteAcceptPage /></Shell></Protected>} />
          <Route path="/tailgates" element={<Protected><Shell><TailgatesPage /></Shell></Protected>} />
          <Route path="/rehearsal" element={<Protected><Shell><RehearsalPage /></Shell></Protected>} />
          <Route path="/rehearsal/:id" element={<Protected><Shell><RehearsalPage /></Shell></Protected>} />
          <Route path="/live" element={<Protected><Shell><LivePage /></Shell></Protected>} />
          <Route path="/live/:id" element={<Protected><Shell><LiveRoomPage /></Shell></Protected>} />
          <Route path="/profile" element={<Protected><Shell><ProfilePage /></Shell></Protected>} />
          <Route path="/notifications" element={<Protected><Shell><NotificationsPage /></Shell></Protected>} />
          <Route path="/admin" element={<Protected><Shell><AdminPage /></Shell></Protected>} />
          <Route path="/admin/partner-show" element={<Protected><Shell><PartnerPregamePage /></Shell></Protected>} />
          <Route path="/admin/partner-show/:briefId" element={<Protected><Shell><PartnerPregamePage /></Shell></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
